<template>
    <div>
      <Modal 
        v-model="show"
        :width="1000"
        :footer-hide="true"
        title="违约单明细"
        :scrollable="true"
        class="optimized-modal"
      >
        <Spin 
          v-if="innerSpin"
          size="large"
          fix
          class="enhanced-spinner"
        ></Spin>
        
        <Card 
          :padding="0"
          :bordered="false"
          :dis-hover="true"
          class="no-shadow-card"
        >
          <!-- 工具栏区域 -->
          <div class="enhanced-toolbar">
            <div class="left-section">
              <Alert type="info" class="operation-guide" show-icon>
                双击明细列表选择要处理的产品
              </Alert>
            </div>
            
            <div class="right-section">
              <div class="tool-group">
                <label class="tool-label">违约方：</label>
                <RadioGroup v-model="liquidatedDamageModel.breachingParty" type="button" @on-change="breachingPartyChange" class="radio-group">
                    <Radio :label="BREACHING_PARTY_NAME[SEEDFARM]" :disabled="!role(1)" class="radio-button">苗场</Radio>
                    <Radio :label="BREACHING_PARTY_NAME[CUSTOMER]" :disabled="!role(1)" class="radio-button">客户</Radio>
                </RadioGroup>
              </div>
  
              <div class="tool-group">
                <label class="tool-label">违约类型：</label>
                <RadioGroup 
                  v-model="liquidatedDamageModel.type" 
                  type="button" 
                  @on-change="typeChange"
                  class="radio-group"
                >
                  <Radio 
                    :label="0" 
                    :disabled="!role(1) || liquidatedDamageModel.breachingParty === BREACHING_PARTY_NAME[SEEDFARM]"
                    class="radio-button"
                  >
                    全部
                  </Radio>
                  <Radio 
                    :label="1" 
                    :disabled="!role(1) || liquidatedDamageModel.breachingParty === BREACHING_PARTY_NAME[SEEDFARM]"
                    class="radio-button"
                  >
                    部分
                  </Radio>
                </RadioGroup>
                <div class="tool-group" v-if="role(1)&&liquidatedDamageModel.breachingParty === BREACHING_PARTY_NAME[SEEDFARM]">
                  <button class="tool-button" @click="double">双倍赔偿</button>
                </div>
              </div>
            </div>
          </div>
  
          <!-- 主要内容区域 -->
          <div class="content-wrapper">
            <Row :gutter="24" class="table-section">
              <Col span="12">
                <div class="table-container">
                  <h3 class="table-title">订单明细</h3>
                  <StandPaginationTable
                    ref="orderDetail"
                    :columns="orderDetail.columns"
                    :data="orderDetail.data"
                    :loading="orderDetail.loading"
                    :table-search="orderDetail.tableSearch"
                    :table-load-event="getDepositDetail"
                    @on-row-dblclick="addLiquidateDamage"
                    summaryLabelField="productName"
                    :summaryFields="['orderQuantity', 'depositValue', 'totalAmount', 'deliveryQuantity']"
                    :autoHeight="true"
                    :showPage="false"
                    border
                    class="enhanced-table"
                  />
                </div>
              </Col>
              
              <Col span="12">
                <div class="table-container">
                  <h3 class="table-title">违约单明细</h3>
                  <StandPaginationTable
                    ref="liquidateDamageTable"
                    :columns="liquidatedDamageDetail.columns"
                    :data="liquidatedDamageDetail.data"
                    :loading="liquidatedDamageDetail.loading"
                    :table-search="liquidatedDamageDetail.tableSearch"
                    :table-load-event="getLiquidateDamageDetail"
                    summaryLabelField="productName"
                    :summaryFields="['depositValue', 'totalAmount', 'liquidatedDamageQuantity', 'liquidatedDamageValue']"
                    :autoHeight="true"
                    :showPage="false"
                    border
                    class="enhanced-table"
                  />
                </div>
              </Col>
            </Row>
  
            <!-- 费用信息区域 -->
            <div class="financial-section">
              <div class="financial-card">
                <h3 class="section-title">费用信息</h3>
                
                <Row :gutter="24" class="amount-row">
                  <Col span="6">
                    <div class="amount-group">
                      <div class="amount-item">
                        <label class="amount-label">已付定金：</label>
                        <span class="amount-value">{{ orderModel.paymentSum }}</span>
                      </div>
                    </div>
                  </Col>
                  <Col span="6">
                    <div class="amount-group">
                      <div class="amount-item">
                        <label class="amount-label">已发定金：</label>
                        <span class="amount-value">{{ orderModel.deliveryDeposit }}</span>
                      </div>
                      <div class="amount-item">
                        <label class="amount-label">剩余定金：</label>
                        <span class="amount-value">{{ orderModel.remainDeposit }}</span>
                      </div>
                    </div>
                  </Col>
                  
                  <Col span="12">
                    <div class="amount-input-group">
                      <label class="amount-label">违约金额：</label>
                      <InputNumber 
                        v-model="liquidatedDamageModel.liquidatedDamageValue" 
                        class="amount-input"
                        :disabled="!(role(1)||role(2))"
                        :min="0"
                      ></InputNumber>
                    </div>
                  </Col>
                </Row>
  
                <template v-if="liquidatedDamageModel.otherFeeShow">
                  <Alert type="warning" class="fee-notice" show-icon>
                    包装费和配送费默认退给客户
                  </Alert>
                  
                  <Row :gutter="24" class="fee-row">
                    <Col span="12">
                      <Tooltip content="默认退给客户" placement="top">
                        <div class="fee-item">
                          <label class="fee-label">包装费：</label>
                          <span class="fee-value">{{ orderModel.packageFee }}</span>
                        </div>
                      </Tooltip>
                    </Col>
                    
                    <Col span="12">
                      <Tooltip content="默认退给客户" placement="top">
                        <div class="fee-item">
                          <label class="fee-label">配送费：</label>
                          <span class="fee-value">{{ orderModel.deliveryFee }}</span>
                        </div>
                      </Tooltip>
                    </Col>
                  </Row>
                </template>
  
                <div class="remark-section">
                  <label class="remark-label">备注：</label>
                  <Input 
                    type="textarea" 
                    v-model="liquidatedDamageModel.liquidatedDamageRemark" 
                    :rows="3"
                    placeholder="请输入备注信息..."
                    class="remark-input"
                  ></Input>
                </div>
              </div>
            </div>
  
            <!-- 操作按钮 -->
            <div class="action-buttons">
              <Button 
                type="primary" 
                class="action-btn"
                v-if="role(1)" 
                @click="saveLiquidatedDamageDetail()"
              >
                <Icon type="md-add" /> 保存
              </Button>
              
              <Button 
                type="primary" 
                class="action-btn"
                v-if="role(2)" 
                @click="submitLiquidatedDamageDetail()"
              >
                <Icon type="md-send" /> 提交
              </Button>
              
              <Button 
                type="info" 
                class="action-btn"
                v-if="role(3)" 
                @click="saveAndSubmitLiquidatedDamageDetail()"
              >
                <Icon type="md-done-all" /> 保存并提交
              </Button>
              
              <Button 
                type="warning" 
                class="action-btn"
                v-if="role(4)" 
                @click="auditLiquidatedDamageDetail()"
              >
                <Icon type="md-checkmark" /> 审核
              </Button>
              
              <Button 
                type="error" 
                class="action-btn"
                v-if="role(5)" 
                @click="close()"
              >
                <Icon type="md-close" /> 关闭
              </Button>
            </div>
          </div>
        </Card>
      </Modal>
      
      <AuditModal ref="auditModal" @audit="audit"/>
    </div>
</template>
<script>
import search from '@/libs/search';
import SearchCard from '@/components/searchCard/searchCard.vue';
import StandPaginationTable from '@/components/pagination-table/pagination-table.vue';
import { formatDateString, formatDateTimeString, calculate } from '@/libs/logic';
import {
    dateRender,
    dateTimeRender
} from '@/libs/tableRowTimeRender';
import {
    getDepositBillOrderList
} from '@/api/modules/preSale-management/preSale-order';
import {
    getDepositBillOrderDetail, getOriginalDepositBillInfo
} from '@/api/modules/preSale-management/deposit-order';
import { getLiquidatedDamageDetail, saveLiquidatedDamage, auditLiquidatedDamage, updateLiquidatedDamage, saveAndSubmitLiquidatedDamage, submitLiquidatedDamage } from '@/api/modules/liquidated-damage/liquidated-damage';
import { BILL_TYPE_DEPOSIT, BILL_TYPE_DELIVERY, BILL_TYPE, CUSTOMER, SEEDFARM, BREACHING_PARTY, BREACHING_PARTY_NAME, ALL, PART, TYPE_NAME } from '@/view/common/liquidated-damage';
import { SAVE, SUBMIT, PASS, REJECT, AUDIT_STATUS } from '@/view/common/audit';
import { PAYID_ATATUS } from '@/view/common/deposit';
import AuditModal from '@/view/modal/audit-modal.vue';
export default {
    name: 'LiquidatedDamageOrderDetail',
    components: {
        StandPaginationTable,
        AuditModal
    },
    data() {
        return {
            BILL_TYPE,
            CUSTOMER, 
            SEEDFARM, 
            BREACHING_PARTY,
            BREACHING_PARTY_NAME,
            TYPE_NAME,
            ALL,
            show: false,
            innerSpin: false,
            pageLoading: false,
            orderModel:{
                packageFee: 0,
                deliveryFee: 0,
                deliveryDeposit: 0,
                remainDeposit: 0,
                paidStatus: '',
                paymentSum: 0
            },
            liquidatedDamageModel:{
                mode: '',
                relateRecordId: '',
                relateRecordCode: '',
                code: '',
                liquidatedDamagesUuid: '',
                auditStatus: -1,
                billType: '',
                type: 0,
                breachingParty: 0,
                depositValue: 0,
                deliveryValue: 0,
                deliveryDeposit: 0,
                remainDeposit: 0,
                otherFeeShow: false,
                liquidatedDamageRemark: '',
                liquidatedDamageValue: 0,
                packageFee: 0,
                deliveryFee: 0
            },
            liquidatedDamageDetail:{
                columns: [
                    {
                        title: "品种编号",
                        key: "productCode"
                    },
                    {
                        title: "品种名称",
                        key: "productName"
                    },
                    /*{
                        title: '定金金额',
                        align: 'center',
                        key: 'depositValue',
                        render: (h, params) => {
                            const { depositValue } = params.row;
                            return h('div', [
                                h('span', {}, depositValue.toFixed(2))
                            ]);
                        }
                    },*/
                    /* {
                        title: '总金额',
                        align: 'center',
                        key: 'totalAmount'
                    }, */
                    {
                        title: '违约数量',
                        align: 'center',
                        decimalPlaces: 0,
                        quantity: true,
                        width: 85,
                        key: 'liquidatedDamageQuantity',
                        render: (h, params) => {
                            const { type } = this.liquidatedDamageModel;
                            const { mode, liquidatedDamageQuantity, liquidatedDamagePrice, quantity } = params.row;
                            return h('InputNumber', {
                                props: {
                                    value: liquidatedDamageQuantity,
                                    disabled: type == TYPE_NAME[ALL] || !this.role(1) ? true : false
                                },
                                style:{
                                    textAlign: "left",
                                    size: "small"
                                },
                                on: {
                                    'on-change': val => {
                                        this.liquidatedDamageDetail.data[params.index].liquidatedDamageQuantity = val;
                                    },
                                    'on-blur': () =>{
                                        let newValue = liquidatedDamageQuantity;
                                        if (liquidatedDamageQuantity > quantity) {
                                            newValue = quantity;
                                            this.$Message.error({
                                                content: "违约数量不能超过定金单产品数量",
                                                duration: 5
                                            });
                                        }
                                        else if (liquidatedDamageQuantity < 0) {
                                            newValue = 0;
                                            this.$Message.error({
                                                content: "数量不能小于零",
                                                duration: 5
                                            });
                                        }
                                        this.liquidatedDamageDetail.data[params.index].liquidatedDamageQuantity = newValue;
                                    }
                                }
                            });
                        }
                    },
                    /*{
                        title: '违约金额',
                        align: 'center',
                        width: 85,
                        key: 'liquidatedDamageValue',
                        render: (h, params) => {
                            const { mode, type, breachingParty } = this.liquidatedDamageModel;
                            return h('InputNumber', {
                                props: {
                                    value: params.row.liquidatedDamageValue,
                                    'active-change': false,
                                    disabled: (breachingParty === BREACHING_PARTY_NAME[CUSTOMER] && type === TYPE_NAME[ALL]) || !this.role(1) ? true : false,
                                },
                                style:{
                                    textAlign: "left",
                                    size: "small"
                                },
                                on: {
                                    'on-change': val => {
                                        this.liquidatedDamageDetail.data[params.index].liquidatedDamageValue = val;
                                        //this.checkLimit(params.index, params.row, val);
                                    },
                                    'on-blur': () =>{
                                        this.checkLimit(params.index, params.row, params.row.liquidatedDamageValue);
                                    }
                                }
                            });
                        }
                    },*/
                    {
                        title: '备注',
                        align: 'center',
                        width: 130,
                        key: 'remark',
                        render: (h, params) => {
                            return h('Input', {
                                props: {
                                    value: params.row.remark,
                                    disabled: this.role(1) ? false : true
                                },
                                style:{
                                    textAlign: "left",
                                    size: "small"
                                },
                                on: {
                                    input: val => {
                                        this.liquidatedDamageDetail.data[params.index].remark = val;
                                    }
                                }
                            });
                        }
                    },
                    {
                        title: '操作',
                        key: 'action',
                        align: 'center',
                        width: 60,
                        render: (h, params) => {
                            return h('div', [
                                h('Button', {
                                    props: {
                                        type: 'text',
                                        size:'small',
                                        shape: 'circle'
                                    },
                                    style:{
                                        display: this.role(1) && this.liquidatedDamageModel.breachingParty != BREACHING_PARTY_NAME[SEEDFARM] ? 'inline' : 'none'
                                    },
                                    on: {
                                        click: () => {
                                            const { row, index } = params;
                                            this.deleteLiquidatedDamageDetail(row, index);
                                        }
                                    }
                                }, '删除')
                            ]);
                        }
                    }
                ],
                data: [],
                loading: false,
                tableSearch: search.getSearchParams()
            },
            orderDetail: {
                columns: [
                    {
                        title: '序号',
                        align: 'center',
                        width:'50',
                        render:(h, params) =>{
                            return h('span', params.index+1);
                        }
                    },
                    {
                        title: "品种编号",
                        key: "productCode"
                    },
                    {
                        title: "品种名称",
                        key: "productName"
                    },
                    {
                        title: "单位",
                        key: "unitName"
                    },
                    {
                        title: '已发货量',
                        align: 'center',
                        quantity: true,
                        key: 'deliveryQuantity',
                        decimalPlaces: 0,
                        render: (h, params) => {
                            const { deliveryQuantity } = params.row; 
                            return h('Tooltip', {
                                props: {
                                    trigger: 'hover',
                                    content: `已发货数量不可违约`,
                                    transfer: true,
                                    placement: 'top-start'
                                }
                            }, [
                                h('span', deliveryQuantity)
                            ]);
                        }
                    },
                    {
                        title: '剩余数量',
                        align: 'center',
                        quantity: true,
                        key: 'orderQuantity',
                        decimalPlaces: 0
                        /* render: (h, params) => {
                            const { orderQuantity, deliveryQuantity } = params.row; 
                            return h('Tooltip', {
                                props: {
                                    trigger: 'hover',
                                    content: `发货数量是 ${deliveryQuantity}`,
                                    transfer: true
                                }
                            }, [
                                h('span', orderQuantity)
                            ]);
                        } */
                    }
                    /* {
                        title: '小计金额',
                        align: 'center',
                        key: 'totalAmount'
                    } */
                    /* {
                        title: '定金比率(%)',
                        align: 'center',
                        key: 'depositBillDetailDepositRatio',
                        render:(h, params) =>{
                            return h('span', calculate(params.row.depositBillDetailDepositRatio, 100, "*").toFixed(2));
                        }
                    }, */
                    /* {
                        title: '定金金额',
                        align: 'center',
                        key: 'depositValue',
                        render: (h, params) => {
                            const { depositValue } = params.row;
                            return h('Tooltip', {
                                props: {
                                    trigger: 'hover',
                                    content: `剩余定金金额（减去已发货定金）`,
                                    transfer: true,
                                    placement: 'top-start'
                                }
                            }, [
                                h('span', {}, depositValue.toFixed(2))
                            ]);
                        }
                    } */
                ],
                data: [],
                loading: false,
                tableSearch: search.getSearchParams()
            }
        };
    },
    computed: {
        
    },
    mounted() {
    },
    methods: {
        formatDateString,
        formatDateTimeString,
        calculate,
        role(r){
          
            const { paidStatus } = this.orderModel;
            const { mode, auditStatus } = this.liquidatedDamageModel;
            switch(r){
                ///保存
                case 1:
                    return this.$checkAccessArray(['G']) && (mode==='add' || (mode == 'handle' && [SAVE, REJECT].includes(auditStatus)));
                ///提交
                case 2:
                    return this.$checkAccessArray(['G']) && mode==='handle' && auditStatus===SAVE;
                ///保存并提交
                case 3:
                    return this.$checkAccessArray(['G']) && (mode==='add' || (mode == 'handle' && [REJECT].includes(auditStatus)));
                ///审核
                case 4:
                    return this.$checkAccessArray(['B']) && mode==='handle' && auditStatus===SUBMIT;
                ///关闭
                case 5:
                    return true;
                ///查看
                case 6:
                    return mode==='view';
                default:
                    return false;
            }
        },
        showModal(mode, billType, Uuid, code, liquidatedDamagesUuid = "") {
            this.liquidatedDamageModel.mode = mode;
            this.liquidatedDamageModel.otherFeeShow = false;
            this.initLiquidatedDamageModel(Uuid, code, billType, liquidatedDamagesUuid)
                .initOrder()
                .init();
        },
        initLiquidatedDamageModel(Uuid, code, billType, liquidatedDamagesUuid, liquidatedDamageValue = 0, remark = "", packageFee = 0, deliveryFee = 0, type = 0, breachingParty = 0, auditStatus = -1) {
            Object.assign(this.liquidatedDamageModel, {
                relateRecordId: Uuid,
                relateRecordCode: code,
                billType: billType,
                code: code,
                type: type,
                breachingParty: breachingParty,
                liquidatedDamagesUuid: liquidatedDamagesUuid,
                packageFee: packageFee,
                deliveryFee: deliveryFee,
                auditStatus: auditStatus,
                liquidatedDamageRemark: remark,
                liquidatedDamageValue: liquidatedDamageValue
            });
            
            return this;
        },
        init() {
            this.orderDetail.data.splice(0);
            this.getDepositDetail();

            if (this.liquidatedDamageModel.mode !== "add") {
                this.getLiquidateDamageDetail();
            }
            this.show = true;
            return this;
        },
        initOrder(packageFee = 0, deliveryFee = 0, deliveryDeposit = 0, remainDeposit = 0) {
            this.orderModel = { packageFee, deliveryFee, deliveryDeposit, remainDeposit };
            return this;
        },
        getLiquidateDamageDetail() {
            this.liquidatedDamageDetail.loading = true;
            this.liquidatedDamageDetail.tableSearch = search.getSearchParams({ closeAllConditions: true });
            search.globalAddFilterConditionKey(this.liquidatedDamageDetail.tableSearch, "ld.liquidatedDamagesUuid", "eq", this.liquidatedDamageModel.liquidatedDamagesUuid, true);

            getLiquidatedDamageDetail(this.liquidatedDamageDetail.tableSearch)
                .then(res => {
                    if (res.code === 0) {
                        const { liquidatedDamagesRelateRecordId, liquidatedDamagesRelateRecordCode, billType, type, breachingParty, liquidatedDamagesUuid, packageFee, deliveryFee, auditStatus, liquidatedDamageRemark, liquidatedDamagesTotalAmount } = res.data.liquidatedDamageModel;
                        this.liquidatedDamageDetail.data = res.data.records;

                        this.initLiquidatedDamageModel(
                            liquidatedDamagesRelateRecordId, liquidatedDamagesRelateRecordCode, billType,
                            liquidatedDamagesUuid, liquidatedDamagesTotalAmount, liquidatedDamageRemark,
                            packageFee, deliveryFee, type ? TYPE_NAME[PART] : TYPE_NAME[ALL], breachingParty, auditStatus
                        );
                        
                        search.globalSetFilterRecordCount(this.liquidatedDamageDetail.tableSearch, res.data.filterRecordCount);
                    }

                    this.liquidatedDamageDetail.loading = false;
                });
        },
        getDepositDetail() {
            this.innerSpin = true;
            this.orderDetail.loading = true;
            getOriginalDepositBillInfo({ depositBillRecordUuid: this.liquidatedDamageModel.relateRecordId })
                .then(res => {
                    this.innerSpin = false;
                    if (res.code === 0) {
                        const { depositBillDetailVoList, depositBillVo, deliveryValue, packageValue } = res.data;
                        // 处理每个明细
                        depositBillDetailVoList.forEach(e => {
                            this.processDepositDetail(e);
                        });

                        //更新order
                        this.setOrderModel(depositBillVo, deliveryValue, packageValue);
                        this.liquidatedDamageModel.paymentSum = depositBillVo.depositBillRecordDepositPaymentSum;
                        if (this.liquidatedDamageModel.mode === "add") this.typeChange();
                        this.orderDetail.loading = false;
                    }
                })
                .catch(() => {
                    this.innerSpin = false;
                });
        },
        setOrderModel(depositBillVo, deliveryValue, packageValue) {
            this.$set(this.orderModel, 'paidStatus', depositBillVo.depositBillRecordPaidStatus);
            this.$set(this.orderModel, 'paymentSum', depositBillVo.depositBillRecordDepositPaymentSum);
            this.$set(this.orderModel, 'deliveryDeposit', depositBillVo.deliveryValue);
            this.$set(this.orderModel, 'remainDeposit', depositBillVo.remainDeposit);

            if (this.orderModel.paidStatus === PAYID_ATATUS.ALL) {
                this.$set(this.orderModel, 'packageFee', packageValue);
                this.$set(this.orderModel, 'deliveryFee', deliveryValue);
            }
            this.copyOrderToLiquidatedDamage();
        },
        processDepositDetail(detail) {
            const { depositBillDetailUuid, depositBillDetailCode, depositBillDetailProductUuid, depositBillDetailProductCode, depositBillDetailProductName, depositBillDetailProductUnit, depositBillDetailOrderQuantity, deliveryQuantity, totalAmount, depositValue } = detail;

            this.orderDetail.data.push({
                objectDetailUuid: depositBillDetailUuid,
                objectDetailCode: depositBillDetailCode,
                productUuid: depositBillDetailProductUuid,
                productCode: depositBillDetailProductCode,
                productName: depositBillDetailProductName,
                unitName: depositBillDetailProductUnit,
                orderQuantity: depositBillDetailOrderQuantity,
                deliveryQuantity,
                quantity: depositBillDetailOrderQuantity,
                price: parseFloat(Number(detail.depositBillDetailProductPrice).toFixed(2)),
                totalAmount: parseFloat(Number(totalAmount).toFixed(2)),
                depositValue,
                rowClass: deliveryQuantity > 0 ? 'gray-row' : ''
            });
        },
        addLiquidateDamage(row, index) {
            const { objectDetailUuid, productUuid, productCode, productName, unitName, quantity, depositValue, totalAmount } = row;
            const found = this.liquidatedDamageDetail.data.some(item => item.productCode === productCode);
            if (found || quantity==0) {
                return;
            }
            //let liquidatedDamagePrice = calculate(depositValue, quantity, "/");
            this.liquidatedDamageDetail.data.push({
                objectDetailUuid: objectDetailUuid,
                productUuid: productUuid,
                productCode: productCode,
                productName: productName,
                unitName: unitName,
                orderQuantity: quantity,
                depositValue: depositValue,
                totalAmount: totalAmount,
                quantity: parseFloat(quantity),
                liquidatedDamageQuantity: parseFloat(quantity),
                //liquidatedDamagePrice: liquidatedDamagePrice,
                remark:""
            });
        },
        turnToAll(){
            if(this.liquidatedDamageDetail.data.length === this.orderDetail.data.length) this.liquidatedDamageModel.type = 0;
            return this;
        },
        deleteLiquidatedDamageDetail(row, index) {
            const { type } = this.liquidatedDamageModel;
            if(!type){
                this.liquidatedDamageModel.type = 1;
            }
            this.liquidatedDamageDetail.data.splice(index, 1);
        },
        checkAmount(){
            const { paidStatus } = this.orderModel;
            const { data } = this.liquidatedDamageDetail;
            const invalidProducts = data.filter(e => {
                return (paidStatus === PAYID_ATATUS.ALL && e.liquidatedDamageValue < e.depositValue);
            }).map(e => e.productName);

            if (invalidProducts.length > 0) {
                this.$Message.error({
                    content: `${invalidProducts.join('、')}的金额不能小于定金金额`,
                    duration: 5
                });
                return false;
            }
            return true;
        },
        copyOrderToLiquidatedDamage() {
            
            this.$set(this.liquidatedDamageModel, 'deliveryDeposit', this.orderModel.deliveryDeposit);
            this.$set(this.liquidatedDamageModel, 'remainDeposit', this.orderModel.remainDeposit);

            if (this.liquidatedDamageModel.mode === "add") {
                this.$set(this.liquidatedDamageModel, 'liquidatedDamageValue', this.orderModel.remainDeposit);
            }

            if (this.orderModel.paidStatus === PAYID_ATATUS.ALL) {
                
                this.$set(this.liquidatedDamageModel, 'packageFee', this.orderModel.packageFee);
                this.$set(this.liquidatedDamageModel, 'deliveryFee', this.orderModel.deliveryFee);

                if (this.orderModel.packageFee > 0 || this.orderModel.deliveryFee > 0) {
                    this.$set(this.liquidatedDamageModel, 'otherFeeShow', true);
                }
            }
        },
        saveLiquidatedDamageDetail() {
            if(!this.checkLimit()) return;
            const { data } = this.liquidatedDamageDetail;
            this.liquidatedDamageModel.liquidatedDamageDetailDtos = data;
            this.$Spin.show();
            saveLiquidatedDamage(this.liquidatedDamageModel)
                .then(res => {
                    this.$Spin.hide();
                    if (res.code === 0) {
                        this.$Modal.success({
                            title: '保存成功',
                            content: '',
                            okText: '关闭'
                        });
                        this.removeModal();
                        this.refreshParent();
                    }
                    this.close();
                })
                .catch(() => {
                    this.$Spin.hide();
                });
        },
        removeModal(){
            setTimeout(() => {
                this.$Modal.remove();
            }, 800);
        },
        refreshParent(){
            this.$parent.init();
        },
        saveAndSubmitLiquidatedDamageDetail(){
            if(!this.checkLimit()) return;
            const { data } = this.liquidatedDamageDetail;
            this.liquidatedDamageModel.liquidatedDamageDetailDtos = data;
            this.$Spin.show();
            saveAndSubmitLiquidatedDamage(this.liquidatedDamageModel).then(res => {
                this.$Spin.hide();
                if (res.code === 0) {
                    this.$Modal.success({
                        title: '保存成功',
                        content: ''
                    });
                    this.removeModal();
                    this.refreshParent();
                }
                this.show = false;
            }).catch(() => {
                this.$Spin.hide();
            });
        },
        typeChange(){
            const { type } = this.liquidatedDamageModel;
            this.liquidatedDamageDetail.data.splice(0);
            if(!type) this.orderDetail.data.forEach(e => this.addLiquidateDamage(e));
            this.checkLimit();
        },
        breachingPartyChange(){
            this.liquidatedDamageModel.type = 0;
            this.typeChange();
            this.feeChange();
        },
        feeChange(){
            if(this.liquidatedDamageModel.breachingParty==BREACHING_PARTY_NAME[SEEDFARM]){
                this.liquidatedDamageModel.packageFee = this.orderModel.packageFee;
                this.liquidatedDamageModel.deliveryFee = this.orderModel.deliveryFee;
            }
            else{
                this.liquidatedDamageModel.packageFee = 0;
                this.liquidatedDamageModel.deliveryFee = 0;
            }
        },
        checkLimit() {
            const { paidStatus, remainDeposit } = this.orderModel;
            const { liquidatedDamageValue, breachingParty } = this.liquidatedDamageModel;
            
            // 定义验证规则
            const VALIDATION_RULES = {
                [BREACHING_PARTY_NAME[SEEDFARM]]: {
                    [PAYID_ATATUS.ALL]: {
                        min: {
                            value: remainDeposit,
                            error: "苗场违约不能少于剩余定金"
                        },
                        max: {
                            value: 2 * remainDeposit,
                            error: "苗场违约不能大于两倍剩余定金"
                        }
                    },
                    [PAYID_ATATUS.DEPOSIT]: {
                        min: {
                            value: remainDeposit,
                            error: "苗场违约不能少于剩余定金"
                        },
                        max: {
                            value: 2 * remainDeposit,
                            error: "苗场违约不能大于两倍剩余定金"
                        }
                    }
                },
                [BREACHING_PARTY_NAME[CUSTOMER]]: {
                    [PAYID_ATATUS.ALL]: {
                        max: {
                            value: remainDeposit,
                            error: "客户违约不能大于剩余定金"
                        }
                    },
                    [PAYID_ATATUS.DEPOSIT]: {
                        max: {
                            value: remainDeposit,
                            error: "客户违约不能大于剩余定金"
                        }
                    }
                }
            };

            const rule = VALIDATION_RULES[breachingParty] && VALIDATION_RULES[breachingParty][paidStatus];
            // 如果没有找到对应的规则，直接返回
            if (!rule) return;

            let errorMsg = '';
            let correctedValue = liquidatedDamageValue;

            // 验证最小值
            if (rule.min && rule.min.value !== undefined && liquidatedDamageValue < rule.min.value) {
                errorMsg = rule.min.error;
                correctedValue = rule.min.value;
            }
            
            // 验证最大值（仅在最小值验证未触发时）
            if (!errorMsg && rule.max && rule.max.value !== undefined && liquidatedDamageValue > rule.max.value) {
                errorMsg = rule.max.error;
                correctedValue = rule.max.value;
            }

            // 如果有错误信息，显示错误并更新值
            if (errorMsg) {
                this.$Message.error({ content: errorMsg, duration: 5 });
                this.liquidatedDamageModel.liquidatedDamageValue = correctedValue;
                return false;
            }
            return true;
        },
        submitLiquidatedDamageDetail(){
            if(!this.checkLimit()) return;
            submitLiquidatedDamage({ liquidatedDamagesUuid: this.liquidatedDamageModel.liquidatedDamagesUuid }).then(res=>{
                if (res.code === 0) {
                    this.$Modal.success({
                        title: '提交成功',
                        content: '',
                        closable: false,
                        duration: 3
                    });
                    this.removeModal();
                    this.refreshParent();
                    this.close();
                }
            });
        },
        //审核
        auditLiquidatedDamageDetail(){
            this.$refs.auditModal.showModal(this.liquidatedDamageModel.liquidatedDamagesUuid);
        },
        //审核
        audit(status, remark, id){
            let auditStatus = status=="通过" ? status = PASS : REJECT;
            auditLiquidatedDamage({ liquidatedDamagesUuid: id, auditStatus: auditStatus, auditRemark: remark }).then(res => {
                if (res.code === 0) {
                    this.refreshParent();
                    this.close();
                }
            });
        },
        close(){
            this.show = false;
        },
        packageFeeLimit(){
            if(this.liquidatedDamageModel.packageFee>this.orderModel.packageFee){
                this.$Message.error({
                    content: "输入包装费不能超过订单包装费",
                    duration: 5
                });
                this.liquidatedDamageModel.packageFee = this.orderModel.packageFee;
            }
            else if(this.liquidatedDamageModel.packageFee<0){
                this.$Message.error({
                    content: "输入包装费不能小于零",
                    duration: 5
                });
                this.liquidatedDamageModel.packageFee = 0;
            }
        },
        deliveryFeeLimit(){
            if(this.liquidatedDamageModel.deliveryFee>this.orderModel.deliveryFee){
                this.$Message.error({
                    content: "输入配送费不能超过订单配送费",
                    duration: 5
                });
                this.liquidatedDamageModel.deliveryFee = this.orderModel.deliveryFee;
            }
            else if(this.liquidatedDamageModel.deliveryFee<0){
                this.$Message.error({
                    content: "输入配送费不能小于零",
                    duration: 5
                });
                this.liquidatedDamageModel.deliveryFee = 0;
            }
        },
        double(){
            this.liquidatedDamageModel.breachingParty = BREACHING_PARTY_NAME[SEEDFARM];
            this.liquidatedDamageModel.liquidatedDamageValue = this.orderModel.remainDeposit*2;
            this.liquidatedDamageModel.type = 0;
            this.typeChange();
            this.feeChange();
        }
    }
};
</script>

<style scoped lang="less">

// 颜色变量
@primary-color: #1890ff;
@success-color: #52c41a;
@warning-color: #faad14;
@error-color: #ff4d4f;
@text-primary: rgba(0, 0, 0, 0.85);
@text-secondary: rgba(0, 0, 0, 0.65);
@divider-color: #f0f0f0;
@background-light: #fafafa;
@table-hover: #e6f7ff;

.optimized-modal {
  /deep/ .ivu-modal-header {
    background: @background-light;
    border-bottom: 1px solid @divider-color;
    
    .ivu-modal-header-inner {
      font-size: 18px;
      font-weight: 600;
      color: @text-primary;
    }
  }

  /deep/ .ivu-modal-body {
    padding: 24px;
  }
}

.enhanced-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
  padding: 16px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

  .left-section {
    flex: 1;
    
    .operation-guide {
      border: 0;
      background: #f8f9fa;
    }
  }

  .right-section {
    display: flex;
    gap: 32px;
    
    .tool-group {
      display: flex;
      align-items: center;
      gap: 12px;
      
      .tool-label {
        font-weight: 500;
        color: @text-primary;
      }
      
      .radio-group {
        .radio-button {
          transition: all 0.2s;
          
          &:hover:not(.ivu-radio-disabled) {
            background: @table-hover;
          }
        }
      }
    }
  }
}

.tool-button {
  padding: 8px 24px;
  border-radius: 20px;
  font-weight: 500;
  background: @primary-color;
  color: #fff;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: darken(@primary-color, 10%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
}

.content-wrapper {
  background: #fff;
  border-radius: 8px;
  padding: 16px;
}

.table-section {
  margin-bottom: 24px;
  
  .table-container {
    background: #fff;
    border-radius: 8px;
    padding: 16px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.03);
    
    .table-title {
      margin-bottom: 16px;
      font-size: 16px;
      color: @text-primary;
      font-weight: 600;
    }
  }
}

.enhanced-table {
  /deep/ .ivu-table {
    border-radius: 8px;
    
    th {
      background: @background-light !important;
      font-weight: 600 !important;
    }
    
    tr:hover td {
      background: @table-hover !important;
    }
  }
}

.financial-section {
  margin-top: 24px;
  
  .financial-card {
    background: @background-light;
    border-radius: 8px;
    padding: 24px;
    border: 1px solid @divider-color;
    
    .section-title {
      margin-bottom: 20px;
      font-size: 16px;
      color: @text-primary;
      font-weight: 600;
    }
    
    .amount-row {
      margin-bottom: 20px;
      
      .amount-item {
        display: flex;
        align-items: center;
        margin-bottom: 12px;
        
        .amount-label {
          width: 80px;
          color: @text-primary;
        }
        
        .amount-value {
          color: @primary-color;
          font-weight: 500;
        }
      }
      
      .amount-input-group {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .amount-input  {
          width: 200px;
        }
      }
    }
    
    .fee-notice {
      margin-bottom: 16px;
      border: 0;
    }
    
    .fee-row {
      margin-bottom: 16px;
      
      .fee-item {
        display: flex;
        align-items: center;
        gap: 12px;
        
        .fee-label {
          width: 80px;
          color: @text-primary;
        }
        
        .fee-value {
          color: @warning-color;
          font-weight: 500;
        }
      }
    }
    
    .remark-section {
      margin-top: 20px;
      
      .remark-label {
        display: block;
        margin-bottom: 8px;
        color: @text-primary;
      }
      
      .remark-input {
        /deep/ textarea {
          resize: vertical;
          min-height: 80px;
        }
      }
    }
  }
}

.action-buttons {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
  gap: 16px;
  
  .action-btn {
    padding: 8px 24px;
    border-radius: 20px;
    font-weight: 500;
    transition: all 0.2s;
    
    i {
      margin-right: 8px;
    }
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    }
  }
}

.enhanced-spinner {
  /deep/ .ivu-spin-main {
    .ivu-spin-dot {
      font-size: 48px;
    }
    
    .ivu-spin-text {
      font-size: 16px;
      margin-top: 16px;
    }
  }
}

.no-shadow-card {
  box-shadow: none !important;
}
</style>